import * as React from 'react';
import {
  useEntityList,
  useConfigApp,
  useConfigAppPath,
  useAuthoringEnvironments,
  useResetEntityData,
  useEntitySingleEnvironment,
  BulkActionsProvider,
} from '@web-config-app/core-react';
import {
  DEFAULT_ENTITY_LIST_STATUSES,
  Category,
  hasEntityOperation,
  type EnvironmentKey,
  type DomainEntity,
} from '@web-config-app/core';
import { isUsingConfigAppBackend } from '@web-config-app/common';
import { useNavigate, Link } from 'react-router-dom';
import { EntityListPageViewProps } from '@web-config-app/core-react-ui';
import { useIntl } from '@leagueplatform/locales';
import { Button } from '@leagueplatform/genesis-core';
import { EntityListBulkActionSelect } from '../entity-list-bulk-actions/entity-list-bulk-actions.component';

export type EntityListPageContentsRenderer = (
  props: EntityListPageViewProps,
) => JSX.Element;

interface EntityListPageContentsProps {
  domainEntity: DomainEntity;
  tabId: string;
  moduleId?: string;
  childModuleId?: string;
  environmentKey: EnvironmentKey;
  entityListPageContentsRenderer: EntityListPageContentsRenderer;
}
const EntityListPageContents = ({
  domainEntity,
  entityListPageContentsRenderer,
  moduleId,
  childModuleId,
  tabId,
  environmentKey,
}: EntityListPageContentsProps) => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();

  const generateConfigPaths = useConfigAppPath();
  const resetEntityData = useResetEntityData();

  const { getEntityDetailsPathForId, entityCreatePath } = generateConfigPaths({
    tabId,
    moduleId,
    childModuleId,
    environmentKey,
  });

  const { tenantId, tenantName } = useConfigApp();
  const {
    currentAuthoringEnvironment,
    otherAuthoringEnvironment,
    environmentMap,
    multiEnvironmentAuthoringEnabled,
    getEnvironmentLabel,
  } = useAuthoringEnvironments({
    environmentKey,
  });
  const { getEntitySingleEnvironment } = useEntitySingleEnvironment();
  const { isEntitySingleEnv } = getEntitySingleEnvironment({
    domainEntity,
  });
  const isUsingConfigBE = isUsingConfigAppBackend({
    entityLevelOverride: domainEntity.isUsingConfigAppBackend,
  });

  const defaultStatusFilter = (
    domainEntity.listTable?.defaultStatuses ?? DEFAULT_ENTITY_LIST_STATUSES
  ).join(',');

  const {
    pagination,
    data,
    isError,
    isLoading,
    loadPage,
    onSearch,
    searchQuery,
    statusFilter,
    onStatusFilterChange,
  } = useEntityList({
    entity: domainEntity,
    environment: currentAuthoringEnvironment,
    domainEntityType: isUsingConfigBE ? domainEntity.type : undefined,
    defaultStatusFilter,
  });
  const displayEmptyCard = isError || (!isLoading && !data?.length);

  const pluralEntityLabel = formatMessage(
    { id: domainEntity.labelKey },
    { count: 2 },
  );

  const emptyCardHeading = formatMessage(
    { id: isError ? 'ERROR_ENTITY_TITLE' : 'EMPTY_ENTITY_TITLE' },
    { entity: pluralEntityLabel },
  );

  const emptyCardDescription = formatMessage(
    {
      id: isError ? 'ERROR_ENTITY_DESCRIPTION' : 'EMPTY_ENTITY_DESCRIPTION',
    },
    { entity: pluralEntityLabel, tenant: tenantName },
  );

  const entityName = formatMessage({ id: domainEntity.labelKey }, { count: 2 });

  const availableEnvironments = React.useMemo(
    () =>
      multiEnvironmentAuthoringEnabled && environmentMap instanceof Map
        ? Array.from(environmentMap.values()).map?.((clientInfo) => ({
            id: clientInfo.environmentKey,
            label: clientInfo.label,
            action: () => {
              const { entityListPath } = generateConfigPaths({
                tabId,
                moduleId,
                childModuleId,
                environmentKey: clientInfo.environmentKey,
              });
              navigate(entityListPath);
            },
          }))
        : null,
    [
      multiEnvironmentAuthoringEnabled,
      environmentMap,
      tabId,
      moduleId,
      childModuleId,
      generateConfigPaths,
      navigate,
    ],
  );

  if (domainEntity.category === Category.Hidden) {
    throw new Error(`Entity ${domainEntity.id} list page does not exist`);
  }

  // Using entityListPageContentsRenderer as a JSX component instead of directly calling it
  // fixes a bug: hooks and other React logic inside Renderer are called before
  // parent is rendered if it's called as a function instead of as a JSX component.
  const Renderer = entityListPageContentsRenderer;

  return (
    <BulkActionsProvider
      currentAuthoringEnvironment={currentAuthoringEnvironment}
      entityName={entityName}
      availableEnvironments={availableEnvironments}
      otherAuthoringEnvironment={otherAuthoringEnvironment}
      domainEntityType={domainEntity.type}
      tenantId={tenantId}
      domainEntity={domainEntity}
    >
      <Renderer
        displayEmptyCard={displayEmptyCard}
        emptyCardHeading={emptyCardHeading}
        emptyCardDescription={emptyCardDescription}
        availableEnvironments={availableEnvironments}
        currentAuthoringEnvironment={currentAuthoringEnvironment}
        multiEnvironmentAuthoringEnabled={multiEnvironmentAuthoringEnabled}
        getEntityDetailsPathForId={getEntityDetailsPathForId}
        entityName={entityName}
        pagination={pagination}
        loadPage={loadPage}
        onSearch={onSearch}
        searchQuery={searchQuery}
        statusFilter={statusFilter || defaultStatusFilter}
        defaultStatusFilter={defaultStatusFilter}
        onStatusFilterChange={onStatusFilterChange}
        isUsingConfigAppBackend={isUsingConfigBE}
        isLoading={isLoading}
        data={data}
        domainEntity={domainEntity}
        isEntitySingleEnv={isEntitySingleEnv}
        environmentName={getEnvironmentLabel(environmentKey)}
        actions={
          <>
            <EntityListBulkActionSelect
              entityName={entityName}
              isUsingConfigAppBackend={isUsingConfigBE}
              domainEntity={domainEntity}
              environment={environmentKey}
            />
            {hasEntityOperation(domainEntity, 'create') && (
              <Button
                as={Link}
                to={entityCreatePath}
                onClick={resetEntityData}
                size="medium"
                icon="interfacePlus"
              >
                {formatMessage({ id: 'CREATE' })}
              </Button>
            )}
          </>
        }
      />
    </BulkActionsProvider>
  );
};

EntityListPageContents.displayName = 'EntityListPageContents';

interface EntityListPageContainerProps {
  entityListPageContentsRenderer: EntityListPageContentsRenderer;
  domainEntity: DomainEntity;
  tabId: string;
  moduleId?: string;
  childModuleId?: string;
  environmentKey: EnvironmentKey;
}

export const EntityListPageContainer = ({
  entityListPageContentsRenderer,
  moduleId,
  childModuleId,
  domainEntity,
  tabId,
  environmentKey,
}: EntityListPageContainerProps) => (
  <EntityListPageContents
    domainEntity={domainEntity}
    tabId={tabId}
    moduleId={moduleId}
    childModuleId={childModuleId}
    environmentKey={environmentKey}
    entityListPageContentsRenderer={entityListPageContentsRenderer}
  />
);
